import React, { useEffect, useState, useCallback } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const STATUSES = ['active', 'paused', 'disabled'];

function statusColor(s) {
  if (s === 'active') return 'success';
  if (s === 'paused') return 'warning';
  return 'default';
}

export default function PlatformWebhooksListPage() {
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();
  const [tenants, setTenants] = useState([]);

  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [tenantInput, setTenantInput] = useState('');
  const [appliedTenantId, setAppliedTenantId] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const canAccess = user?.role === 'super_admin' || user?.role === 'analyst';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await platformApiFetch('/api/platform/tenants?pageSize=500&sort=tenant_name&order=asc');
        const json = await res.json().catch(() => ({}));
        if (res.ok && !cancelled) setTenants(json.tenants || []);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page + 1),
      pageSize: String(pageSize),
      sort,
      order
    });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedTenantId) params.set('tenantId', appliedTenantId);
    if (appliedStatus) params.set('status', appliedStatus);
    try {
      const res = await platformApiFetch(`/api/platform/webhooks?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load webhooks');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedQ, appliedTenantId, appliedStatus, sort, order]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedTenantId(tenantInput);
    setAppliedStatus(statusInput);
  };

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canAccess) {
    return <Navigate to="/platform/analytics" replace />;
  }

  const rows = data?.webhooks || [];
  const total = data?.pagination?.total ?? 0;
  const isSuper = user?.role === 'super_admin';

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Webhooks
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cross-tenant outbound webhooks. Secrets are encrypted at rest; only masked values are listed. Super admins can
        create, edit, delete, and run tests.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            sx={{ minWidth: 200 }}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Tenant</InputLabel>
            <Select label="Tenant" value={tenantInput} onChange={(e) => setTenantInput(e.target.value)}>
              <MenuItem value="">All tenants</MenuItem>
              {tenants.map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.tenant_name} ({t.tenant_code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusInput} onChange={(e) => setStatusInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sort</InputLabel>
            <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <MenuItem value="created_at">Created</MenuItem>
              <MenuItem value="last_triggered_at">Last triggered</MenuItem>
              <MenuItem value="webhook_name">Name</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Order</InputLabel>
            <Select label="Order" value={order} onChange={(e) => setOrder(e.target.value)}>
              <MenuItem value="desc">Desc</MenuItem>
              <MenuItem value="asc">Asc</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={applyFilters}>
            Apply
          </Button>
          {isSuper && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/platform/webhooks/new')}>
              New webhook
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Secret</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">View</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {row.tenant_name || row.tenant_id}
                      {row.tenant_code ? (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          ({row.tenant_code})
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{row.webhook_name}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row.target_url}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.secret_masked}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={statusColor(row.status)} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton component={Link} to={`/platform/webhooks/${row.id}`} size="small">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {rows.length === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">No webhooks match.</Typography>
              </Box>
            )}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
